import { useState, useEffect } from "react";
import {
  AnimatedSprite,
  Application,
  Assets,
  Texture,
  Sprite,
  Graphics,
  Rectangle,
  BlurFilter,
  SCALE_MODES,
  TextStyle,
  Text,
  Container,
} from "pixi.js";
import DewanRakyatBg from "@public/pixijs/Dewan_Rakyat.png";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { DropShadowFilter } from "pixi-filters";
import { NextSeo } from "next-seo";
import { Viewport } from "pixi-viewport";
import { toast } from "react-toastify";
import { PARLIAMENT_FACTS } from "@lib/parliamentFacts";
import {
  useAllShortsLazyQuery,
  useAllShortsQuery,
} from "@graphql/generated/graphql";

const WhiteCheckmarkSvg = () => (
  <svg
    width="21"
    height="20"
    viewBox="0 0 21 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.5 0.25C5.1239 0.25 0.75 4.62391 0.75 10C0.75 15.3761 5.1239 19.75 10.5 19.75C15.8761 19.75 20.25 15.3761 20.25 10C20.25 4.62391 15.8761 0.25 10.5 0.25ZM15.5742 6.73234L9.27421 14.2323C9.2051 14.3147 9.11911 14.3812 9.02207 14.4273C8.92502 14.4735 8.81917 14.4983 8.71171 14.5H8.69906C8.59393 14.5 8.48999 14.4778 8.39398 14.435C8.29796 14.3922 8.21201 14.3297 8.14171 14.2516L5.44171 11.2516C5.37314 11.1788 5.3198 11.0931 5.28483 10.9995C5.24985 10.9059 5.23395 10.8062 5.23805 10.7063C5.24215 10.6064 5.26617 10.5084 5.3087 10.4179C5.35123 10.3275 5.41142 10.2464 5.48572 10.1796C5.56002 10.1127 5.64693 10.0614 5.74136 10.0286C5.83578 9.99578 5.93581 9.98219 6.03556 9.98861C6.13531 9.99502 6.23277 10.0213 6.32221 10.0659C6.41166 10.1106 6.49128 10.1726 6.5564 10.2484L8.67937 12.6072L14.4258 5.76766C14.5547 5.61863 14.737 5.52631 14.9334 5.51066C15.1299 5.49501 15.3245 5.55728 15.4754 5.68402C15.6263 5.81075 15.7212 5.99176 15.7397 6.18793C15.7582 6.3841 15.6987 6.57966 15.5742 6.73234Z"
      fill="white"
    />
  </svg>
);

const toastLearnSomethingNew = () => {
  if (toastShown) return;
  toastShown = true;
  toast(
    <div className="align-center flex font-sen">
      <div className="flex flex-col justify-center p-1">
        <WhiteCheckmarkSvg />
      </div>
      <div>
        <p className="text-sm font-bold">You've learned something new!</p>
        <p className="text-sm">Come back tomorrow for more trivia</p>
      </div>
    </div>,
    {
      position: "bottom-center",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: "min-w-[350px] !text-white !font-sen !bg-[#00C1B6]",
      theme: "light",
    }
  );
};

let toastShown = false;

const CLICK_STATE = {
  johari: false,
  fayhsal: false,
  lady: false,
  fadhila: false,
  theBell: false,
};

let IS_LADY_CLICKABLE = true;
function throttle(func, wait) {
  let inThrottle, lastFn, lastTime;
  return function () {
    const context = this,
      args = arguments;
    if (!inThrottle) {
      func.apply(context, args);
      lastTime = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFn);
      lastFn = setTimeout(function () {
        if (Date.now() - lastTime >= wait) {
          func.apply(context, args);
          lastTime = Date.now();
        }
      }, Math.max(wait - (Date.now() - lastTime), 0));
    }
  };
}

// Function to calculate the number of days between two dates
function calculateDaysApart(date1, date2) {
  // Convert both dates to UTC to avoid issues with time zones
  const utcDate1 = Date.UTC(
    date1.getFullYear(),
    date1.getMonth(),
    date1.getDate()
  );
  const utcDate2 = Date.UTC(
    date2.getFullYear(),
    date2.getMonth(),
    date2.getDate()
  );

  // Calculate the difference in milliseconds
  const msDifference = Math.abs(utcDate2 - utcDate1);

  // Convert milliseconds to days
  const daysDifference = Math.floor(msDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
}

const Home = () => {
  const [chosenFact, setChosenFact] = useState(null);
  const [showFact, setShowFact] = useState(false);
  const factDate = new Date("2025-08-08");
  const daysApart = calculateDaysApart(new Date(), factDate);
  factDate.setDate(factDate.getDate() + (daysApart % 21));
  const FACTS = PARLIAMENT_FACTS[factDate.toISOString().slice(0, 10)].sort(
    (x, y) => x.question.length - y.question.length
  );

  const openFactPage = (fact) => {
    setChosenFact(fact);
    setShowFact(true);
    setTimeout(() => {
      toastLearnSomethingNew();
    }, 700);
  };
  const closeFactPage = () => {
    setShowFact(false);
  };

  useEffect(() => {
    (async () => {
      // Create a new application
      const app = new Application();

      // Initialize the application
      await app.init({
        resolution: window.devicePixelRatio || 1, // Set resolution to match the device
        autoDensity: true, // Enable auto-resizing
        background: "#3453DC",
        resizeTo: window,
      });

      if (document.body.querySelector("#canvas").querySelector("canvas")) {
        // const div = document.body.querySelector("#canvas");
        // div.innerHTML = "";
        // div.appendChild(app.canvas);
      } else {
        // Append the application canvas to the document body
        document.body.querySelector("#canvas").appendChild(app.canvas);
      }

      await document.fonts.ready;

      const shortsQueryData = await fetch("https://api.luminews.my/graphql", {
        credentials: "omit",
        headers: {
          "User-Agent":
            "Mozilla/5.0 (Macintosh; Intel Mac OS X 10.15; rv:140.0) Gecko/20100101 Firefox/140.0",
          Accept: "*/*",
          "Accept-Language": "en-US,en;q=0.5",
          "content-type": "application/json",
          "Sec-Fetch-Dest": "empty",
          "Sec-Fetch-Mode": "cors",
          "Sec-Fetch-Site": "cross-site",
          Priority: "u=4",
          Pragma: "no-cache",
          "Cache-Control": "no-cache",
        },
        body: '{"operationName":"AllShorts","variables":{"limit":1,"playlistId":"lumi-parliament"},"query":"query AllShorts($limit: Int, $offset: Int, $after: ID, $playlistId: String) {\\n  allShorts(\\n    offsetAttributes: {limit: $limit, offset: $offset, after: $after}\\n    playlistId: $playlistId\\n  ) {\\n    ...VideoSummary\\n    __typename\\n  }\\n}\\n\\nfragment VideoSummary on Video {\\n  id\\n  imageUrl\\n  externalId\\n  title\\n  pubDate\\n  newsPublisher {\\n    id\\n    name\\n    slug\\n    iconUrl\\n    imageUrl\\n    __typename\\n  }\\n  __typename\\n}\\n"}',
        method: "POST",
        mode: "cors",
      }).then((resp) => resp.json());

      let shortTitle = "";
      let shortId = 380212;
      try {
        shortTitle = shortsQueryData.data.allShorts[0].title;
        shortTitle = shortTitle.split(/\||#/)[0];
        shortId = shortsQueryData.data.allShorts[0].id;
      } catch (e) {}
      const SHORTS_PATH = `/shorts/${shortId}?playlistId=lumi-parliament`;
      const anchor = document.getElementById(
        "open-short-link"
      ) as HTMLAnchorElement;
      anchor.href = `https://luminews.my${SHORTS_PATH}`;

      // create viewport
      const viewport = new Viewport({
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        worldWidth: window.innerWidth,
        worldHeight: window.innerHeight,
        events: app.renderer.events, // the interaction module is important for wheel to work properly when renderer.view is placed or scaled
      });

      // add the viewport to the stage
      app.stage.addChild(viewport);

      // TODO: maybe disable on desktop?
      viewport
        .drag()
        .pinch()
        .clamp({
          left: 0,
          right: 0 + window.innerWidth,
          top: 0,
          bottom: 0 + window.innerHeight,
        })
        .clampZoom({
          maxWidth: window.innerWidth,
          maxHeight: window.innerHeight,
        })
        .wheel()
        .decelerate();

      await Assets.load([
        {
          alias: "background",
          src: DewanRakyatBg.src,
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "mid_chair_1",
          src: window.location.origin + "/pixijs/Mid_Chair_3.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_chair_1",
          src: window.location.origin + "/pixijs/Side_Chair_1.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_chair_2",
          src: window.location.origin + "/pixijs/Side_Chair_2.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_chair_4",
          src: window.location.origin + "/pixijs/Side_Chair_4.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_chair_5",
          src: window.location.origin + "/pixijs/Side_Chair_5.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "mid_table_1",
          src: window.location.origin + "/pixijs/Mid_Table.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_table_1",
          src: window.location.origin + "/pixijs/Side_Table_1.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_table_2",
          src: window.location.origin + "/pixijs/Side_Table_2.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_table_3",
          src: window.location.origin + "/pixijs/Side_Table_3.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "side_table_4",
          src: window.location.origin + "/pixijs/Side_Table_4.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "johari-idle",
          src: window.location.origin + "/pixijs/Idle Speaker Johari.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "johari-animated",
          src: window.location.origin + "/pixijs/Speaker Johari Animation.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "fayhsal-idle",
          src: window.location.origin + "/pixijs/Idle Ahmad Fayhsal.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "fayhsal-animated",
          src: window.location.origin + "/pixijs/Ahmad Fayhsal Animation.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "the-lady-idle",
          src: window.location.origin + "/pixijs/Idle The Lady.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "fadhila-idle",
          src: window.location.origin + "/pixijs/Idle The Secretary.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "fadhila-animated",
          src: window.location.origin + "/pixijs/Fadhila Sidek Animation.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "default-gumi-idle",
          src: window.location.origin + "/pixijs/Idle Default Gumi.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "the-bell-idle",
          src: window.location.origin + "/pixijs/The Bell idle.png",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
        {
          alias: "the-bell-animated",
          src: window.location.origin + "/pixijs/The Bell animated.json",
          texturePreference: {
            resolution: window.devicePixelRatio,
          },
        },
      ]);

      // Create a background sprite.
      const background = Sprite.from("background");

      // Center background sprite anchor.
      background.anchor.set(0.5);
      background.scale.set(0.3);
      // Position the background sprite in the center of the stage.
      background.x = app.screen.width / 2;
      background.y = app.screen.height / 2 + 10;
      // Add the background to the stage.
      viewport.addChild(background);

      const BASE_X = background.x;
      const BASE_Y = background.y;

      const theBell = Sprite.from("the-bell-idle");
      theBell.anchor.set(0.5);
      theBell.scale.set(0.7);
      theBell.position.set(BASE_X + 0, BASE_Y + 0);
      theBell.onpointerdown = function () {
        let loopCount = 0;
        const totalLoops = 20;

        updateDiscoveredItemCountText("theBell");
        theBell.visible = false;
        const animation = gumiAnims["the-bell-animated"];
        animation.gotoAndStop(0);
        animation.visible = true;
        animation.loop = false;
        if (!animation.onComplete) {
          animation.onComplete = () => {
            loopCount++;
            if (loopCount >= totalLoops) {
              animation.stop();
              animation.onComplete = null;
              animation.visible = false;
              theBell.visible = true;
            } else {
              animation.play();
            }
          };
        }
        animation.play();
      };
      theBell.eventMode = "static";
      theBell.cursor = "pointer";
      theBell.zIndex = 10;
      // bigger hit area for phone users
      theBell.hitArea = new Rectangle(10, -185, 60, 60);
      viewport.addChild(theBell);

      const midChair = Sprite.from("mid_chair_1");
      midChair.anchor.set(0.5);
      midChair.scale.set(0.7);
      midChair.position.set(BASE_X + 12.5, BASE_Y + 6.5);
      viewport.addChild(midChair);

      const sideChair1 = Sprite.from("side_chair_1");
      sideChair1.anchor.set(0.5);
      sideChair1.scale.set(0.7);
      sideChair1.position.set(BASE_X + 12.5, BASE_Y + 6.5);
      viewport.addChild(sideChair1);

      const sideChair2 = Sprite.from("side_chair_2");
      sideChair2.anchor.set(0.5);
      sideChair2.scale.set(0.7);
      sideChair2.position.set(BASE_X + 12.5, BASE_Y + 6.5);
      viewport.addChild(sideChair2);

      ["mid_table_1", "side_table_1", "side_table_2"].forEach((spriteName) => {
        const sprite = Sprite.from(spriteName);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        sprite.position.set(BASE_X + 12.5, BASE_Y + 6.5);
        viewport.addChild(sprite);
      });
      ["side_table_3", "side_table_4"].forEach((spriteName) => {
        const sprite = Sprite.from(spriteName);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        sprite.position.set(BASE_X + 12.5, BASE_Y + 12.5);
        viewport.addChild(sprite);
      });

      const idleGumis = [
        {
          alias: "johari-idle",
          json_path:
            window.location.origin + "/pixijs/Idle Speaker Johari.json",
          frame_prefix: "Idle Speaker Johari Blend/Idle Speaker Johari",
          position: [BASE_X - 20, BASE_Y - 5],
          hitAreaPosition: [100, -80],
          scale: 0.6,
          onClick: function () {
            updateDiscoveredItemCountText("johari");
            gumiAnims["johari-idle"].visible = false;
            const animation = gumiAnims["johari-animated"];
            animation.gotoAndStop(0);
            animation.visible = true;
            animation.loop = false;
            animation.play();
            if (!animation.onComplete) {
              animation.onComplete = () => {
                animation.visible = false;
                gumiAnims["johari-idle"].visible = true;
              };
            }
          },
        },
        {
          alias: "johari-animated",
          json_path:
            window.location.origin + "/pixijs/Speaker Johari Animation.json",
          frame_prefix: "PNG Sequence/Speaker Johari Animation",
          position: [BASE_X - 20, BASE_Y - 5],
          scale: 0.6,
          play: false,
          visible: false,
          maxFrames: 20,
        },
        {
          alias: "fayhsal-idle",
          json_path: window.location.origin + "/pixijs/Idle Ahmad Fayhsal.json",
          frame_prefix: `Idle Ahmad Fayhsal Blend/Idle Ahmad Fayhsal`,
          position: [BASE_X + 20, BASE_Y - 5],
          scale: 0.5,
          hitAreaPosition: [-120, -80],
          onClick: function () {
            updateDiscoveredItemCountText("fayhsal");
            gumiAnims["fayhsal-idle"].visible = false;
            const animation = gumiAnims["fayhsal-animated"];
            animation.gotoAndStop(0);
            animation.visible = true;
            animation.loop = false;
            animation.play();

            if (!animation.onComplete) {
              animation.onComplete = () => {
                animation.visible = false;
                gumiAnims["fayhsal-idle"].visible = true;
              };
            }
          },
        },
        {
          alias: "fayhsal-animated",
          json_path:
            window.location.origin + "/pixijs/Ahmad Fayhsal Animation.json",
          frame_prefix: "PNG Sequence/Ahmad Fayhsal Animation",
          position: [BASE_X + 20, BASE_Y - 5],
          scale: 0.5,
          play: false,
          visible: false,
          maxFrames: 50,
        },
        {
          alias: "the-lady-idle",
          json_path: window.location.origin + "/pixijs/Idle The Lady.json",
          frame_prefix: "Idle 01 [Don't Have Interaction]/Idle The Secretary",
          position: [BASE_X + 25, BASE_Y - 5],
          scale: 0.5,
          hitAreaPosition: [-300, -100],
          hitAreaPadding: [50, 80],
          onClick: function (e) {
            if (!IS_LADY_CLICKABLE) return;
            IS_LADY_CLICKABLE = false;
            e.stopPropagation();
            updateDiscoveredItemCountText("lady");

            const sprite = gumiAnims["the-lady-idle"];
            sprite.eventMode = "none";

            // Define movement parameters
            const distanceToMove = 20;
            const totalTime = 300; // in milliseconds (1 second)

            // State variables for the animation
            let startingY = sprite.y;
            let elapsedTime = 0;

            // The function to be called on every frame

            const moveSpriteUp = (deltaTime) => {
              elapsedTime += app.ticker.elapsedMS;
              let progress = elapsedTime / totalTime;

              // Clamp progress to 1 to prevent overshooting
              if (progress >= 1) {
                progress = 1;
                app.ticker.remove(moveSpriteUp);
                console.log("Movement complete!");

                // sprite.y = sprite.y - 20;
                // const [x, y] = [sprite.x - 110, sprite.y + 20];
                // const glow = createGlowGraphic(x, y);
                // glow.zIndex = 1;
                // glow.alpha = 0;
                // viewport.addChild(glow);
              }

              // Apply the ease-out function to the progress
              const easedProgress = 1 - Math.pow(1 - progress, 3);

              // Calculate the new Y position using the eased progress
              const newY = startingY - distanceToMove * easedProgress;

              sprite.y = newY;
            };

            // The function to be called on every frame
            const moveSpriteDown = (deltaTime) => {
              elapsedTime += app.ticker.elapsedMS;
              let progress = elapsedTime / totalTime;

              // Clamp progress to 1 to prevent overshooting
              if (progress >= 1) {
                progress = 1;
                app.ticker.remove(moveSpriteDown);
                // Re-enable clicks after a delay
                setTimeout(() => {
                  IS_LADY_CLICKABLE = true;
                }, 500); // 500ms delay
                console.log("Movement complete!");
              }

              // Apply the ease-out function to the progress
              const easedProgress = 1 - Math.pow(1 - progress, 3);

              // Calculate the new Y position using the eased progress
              const newY = startingY + distanceToMove * easedProgress; // This is the changed line

              sprite.y = newY;
            };

            // Start the animation
            app.ticker.add(moveSpriteUp);

            const backgroundWidth = 160;
            // Create a TextStyle object to define the text's appearance
            const style = new TextStyle({
              fontFamily: "Poppins",
              fontSize: 12,
              fill: "#ffffff", // White color
              wordWrap: true,
              wordWrapWidth: backgroundWidth - 30, // Slightly less than the background width
              align: "center",
              lineHeight: 20,
            });

            // Create the text object
            const text = new Text({
              text: FACTS[0].question,
              style,
            });
            text.anchor.set(0.5); // Center the text's anchor point

            // Create the text object
            const text2 = new Text({
              text: FACTS[1].question,
              style,
            });
            text2.anchor.set(0.5); // Center the text's anchor point

            // Create the rounded rectangle graphic
            const background = new Graphics();
            background.beginFill(0x4a3a3a, 0.7); // The color and opacity of the background
            background.drawRoundedRect(
              0,
              0,
              backgroundWidth,
              text.height + 10,
              20
            ); // x, y, width, height, radius
            background.endFill();

            // Create the rounded rectangle graphic
            const background2 = new Graphics();
            background2.beginFill(0x4a3a3a, 0.7); // The color and opacity of the background
            background2.drawRoundedRect(
              0,
              background.height + 10,
              backgroundWidth,
              text2.height + 10,
              20
            ); // x, y, width, height, radius
            background2.endFill();

            const fullScreenContainer = new Container();
            fullScreenContainer.width = app.screen.width;
            fullScreenContainer.height = app.screen.height;
            fullScreenContainer.eventMode = "static";
            fullScreenContainer.cursor = "pointer";
            fullScreenContainer.once(
              "pointerdown",
              throttle(() => {
                elapsedTime = 0;
                startingY = sprite.y;
                // Start the animation
                app.ticker.add(moveSpriteDown);
                sprite.eventMode = "static";

                // Remove the entire container and its children from the stage
                viewport.removeChild(fullScreenContainer);
                // It's good practice to destroy the container to free up memory
                fullScreenContainer.destroy({ children: true });
              }, 500)
            );

            // Create a Container to hold the textbox elements
            const textbox = new Container();

            // Add the background and text to the container
            textbox.addChild(background);
            textbox.addChild(text);
            textbox.addChild(background2);
            textbox.addChild(text2);
            background.eventMode = "static";
            background.cursor = "pointer";
            background.onpointerdown = function (e) {
              e.stopPropagation();
              openFactPage(FACTS[0]);
            };
            text.eventMode = "static";
            text.cursor = "pointer";
            text.onpointerdown = function (e) {
              e.stopPropagation();
              openFactPage(FACTS[0]);
            };
            background2.eventMode = "static";
            background2.cursor = "pointer";
            background2.onpointerdown = function (e) {
              e.stopPropagation();
              openFactPage(FACTS[1]);
            };
            text2.eventMode = "static";
            text2.cursor = "pointer";
            text2.onpointerdown = function (e) {
              e.stopPropagation();
              openFactPage(FACTS[1]);
            };
            // Center the text within the background
            text.x = background.width / 2;
            text.y = background.height / 2;

            text2.x = background2.width / 2;
            text2.y = background2.height / 2 + text.height + 20;

            // Center the entire textbox on the screen
            textbox.x = (app.screen.width - textbox.width) / 2;
            textbox.y = (app.screen.height - textbox.height) / 2;
            textbox.x = sprite.x - 80;
            textbox.y = sprite.y - 70;

            textbox.zIndex = 3;
            fullScreenContainer.zIndex = 3;
            // Add the container to the stage
            //
            const layerBackground = new Graphics();
            layerBackground.beginFill(0x000000, 0); // Black with 50% opacity
            layerBackground.drawRect(0, 0, app.screen.width, app.screen.height);
            layerBackground.endFill();
            fullScreenContainer.addChild(layerBackground);
            fullScreenContainer.addChild(textbox);
            viewport.addChild(fullScreenContainer);
          },
        },
        {
          alias: "fadhila-idle",
          json_path: window.location.origin + "/pixijs/Idle The Secretary.json",
          frame_prefix: "Idle Fadhila Sidek Blend/Idle Fadhila Sidek",
          position: [BASE_X + 15, BASE_Y + 5],
          scale: 0.5,
          hitAreaPosition: [-70, 30],
          onClick: function (e) {
            e.stopPropagation();
            updateDiscoveredItemCountText("fadhila");
            gumiAnims["fadhila-idle"].visible = false;
            const animation = gumiAnims["fadhila-animated"];
            animation.gotoAndStop(0);
            animation.visible = true;
            animation.loop = false;
            animation.play();
            if (!animation.onComplete) {
              animation.onComplete = () => {
                animation.visible = false;
                gumiAnims["fadhila-idle"].visible = true;
              };
            }

            const sprite = gumiAnims["fadhila-idle"];
            sprite.eventMode = "none";
            const backgroundWidth = 200;
            // Create a TextStyle object to define the text's appearance
            const style = new TextStyle({
              fontFamily: "Poppins",
              fontSize: 12,
              fill: "#ffffff", // White color
              wordWrap: true,
              wordWrapWidth: backgroundWidth - 30, // Slightly less than the background width
              align: "center",
              lineHeight: 20,
            });

            // Create the text object
            const text = new Text({
              text: `▶️Tap to watch:\n${shortTitle}`,
              style,
            });
            text.anchor.set(0.5); // Center the text's anchor point

            // Create the rounded rectangle graphic
            const background = new Graphics();
            background.beginFill(0x4a3a3a, 0.7); // The color and opacity of the background
            background.drawRoundedRect(
              0,
              0,
              backgroundWidth,
              text.height + 10,
              20
            ); // x, y, width, height, radius
            background.endFill();

            const fullScreenContainer = new Container();
            fullScreenContainer.width = app.screen.width;
            fullScreenContainer.height = app.screen.height;
            fullScreenContainer.eventMode = "static";
            fullScreenContainer.cursor = "pointer";
            fullScreenContainer.once(
              "pointerdown",
              throttle(() => {
                // Start the animation
                sprite.eventMode = "static";

                // Remove the entire container and its children from the stage
                viewport.removeChild(fullScreenContainer);
                // It's good practice to destroy the container to free up memory
                fullScreenContainer.destroy({ children: true });
              }, 500)
            );

            // Create a Container to hold the textbox elements
            const textbox = new Container();

            // Add the background and text to the container
            textbox.addChild(background);
            textbox.addChild(text);
            background.eventMode = "static";
            background.cursor = "pointer";
            text.eventMode = "static";
            text.cursor = "pointer";
            const openVideo = function (e) {
              e.stopPropagation();
              // Start the animation
              sprite.eventMode = "static";

              // Remove the entire container and its children from the stage
              viewport.removeChild(fullScreenContainer);
              // It's good practice to destroy the container to free up memory
              fullScreenContainer.destroy({ children: true });

              const anchor = document.getElementById(
                "open-short-link"
              ) as HTMLAnchorElement;
              const userAgent = window.navigator.userAgent.toLowerCase();
              const isIos = /ip(ad|hone|od)/.test(userAgent);
              const isSafari = /safari/.test(userAgent);
              const isIosWebview = isIos && !isSafari;

              if (isIosWebview) {
                anchor.href = `luminews://luminews.my${SHORTS_PATH}`;
              }

              if (window.matchMedia("(min-width: 769px)").matches) {
                anchor.target = "_blank";
                anchor.href = `https://luminews.my${SHORTS_PATH}`;
              }

              anchor.click();
            };
            text.onpointerdown = openVideo;
            background.onpointerdown = openVideo;
            // Center the text within the background
            text.x = background.width / 2;
            text.y = background.height / 2;

            // Center the entire textbox on the screen
            textbox.x = sprite.x - 95;
            textbox.y = sprite.y - 75;

            textbox.zIndex = 3;
            fullScreenContainer.zIndex = 3;
            // Add the container to the stage
            //
            const layerBackground = new Graphics();
            layerBackground.beginFill(0x000000, 0); // Black with 50% opacity
            layerBackground.drawRect(0, 0, app.screen.width, app.screen.height);
            layerBackground.endFill();
            fullScreenContainer.addChild(layerBackground);
            fullScreenContainer.addChild(textbox);
            viewport.addChild(fullScreenContainer);
          },
        },
        {
          alias: "fadhila-animated",
          json_path:
            window.location.origin + "/pixijs/Fadhila Sidek Animation.json",
          frame_prefix: "PNG Sequence/Fadhila Sidek Animation",
          position: [BASE_X + 15, BASE_Y + 5],
          scale: 0.5,
          play: false,
          visible: false,
          maxFrames: 30,
        },
        {
          alias: "default-gumi-idle",
          json_path: window.location.origin + "/pixijs/Idle Default Gumi.json",
          frame_prefix: "Idle 02 [Don't Have Interaction]/Idle Default Gumi",
          position: [BASE_X + 0, BASE_Y + 5],
          scale: 0.5,
          zIndex: 0,
        },
        {
          alias: "the-bell-animated",
          json_path: window.location.origin + "/pixijs/The Bell animated.json",
          frame_prefix: "",
          position: [BASE_X + 25, BASE_Y - 110],
          scale: 0.03,
          play: true,
          visible: false,
          maxFrames: 4,
          zIndex: 0,
        },
      ];

      const gumiAnims = {};
      const gumiLoadFunction = async (data) => {
        const frames = [];

        for (let i = 0; i <= (data.maxFrames || 160); i++) {
          const val = i < 10 ? `00${i}` : i < 100 ? `0${i}` : i;
          // Magically works since the spritesheet was loaded with the pixi loader

          frames.push(Texture.from(`${data.frame_prefix}0${val}.png`));
        }

        const anim = new AnimatedSprite(frames);
        anim.position.set(data.position[0], data.position[1]);

        // anim.animationSpeed = 0.3;
        if (data.play != false) anim.play();
        if (data.visible != undefined) anim.visible = false;
        anim.label = data.alias;
        if (data.onClick) {
          anim.eventMode = "static";
          anim.cursor = "pointer";
          const hitAreaPadding = data.hitAreaPadding || [20, 20];
          const hitAreaPaddingX = hitAreaPadding[0];
          const hitAreaPaddingY = hitAreaPadding[1];
          anim.hitArea = new Rectangle(
            data.hitAreaPosition[0],
            data.hitAreaPosition[1],
            // frames[0].trim.x,
            // frames[0].trim.y,
            frames[0].trim.width + hitAreaPaddingX,
            frames[0].trim.height + hitAreaPaddingY
          );

          anim.onpointerdown = data.onClick;
        }
        anim.anchor.set(0.5);
        if (data.scale) anim.scale.set(data.scale);
        anim.zIndex = data.zIndex == undefined ? 2 : data.zIndex;
        viewport.addChild(anim);

        return anim;
      };

      for await (const gumi of idleGumis) {
        const alias = gumi.alias;
        const anim = await gumiLoadFunction(gumi);

        gumiAnims[gumi.alias] = anim;
      }

      ["side_chair_4", "side_chair_5"].forEach((spriteName) => {
        const sprite = Sprite.from(spriteName);
        sprite.anchor.set(0.5);
        sprite.scale.set(0.7);
        sprite.position.set(BASE_X + 12.5, BASE_Y + 12.5);
        viewport.addChild(sprite);
      });

      const hintBox = new Graphics();
      hintBox.beginFill(0xffffff, 0.075); // The color and opacity of the background
      hintBox.drawRoundedRect(0, 0, 250, 70, 20); // x, y, width, height, radius
      hintBox.endFill();
      hintBox.x = app.screen.width / 2 - hintBox.width / 2;
      hintBox.y = background.y - 260;

      hintBox.filters = [
        new DropShadowFilter({
          // The color of the shadow (a hex value)
          color: 0x000000,
          // The blur amount of the shadow
          blur: 10,
          // The opacity of the shadow
          alpha: 0.5,
        }),
      ];

      // Create a TextStyle object to define the text's appearance
      const style = new TextStyle({
        fontFamily: "Poppins",
        fontSize: 14,
        fill: "#ffffff", // White color
        wordWrap: true,
        wordWrapWidth: 200, // Slightly less than the background width
        align: "center",
        lineHeight: 20,
      });

      // Create the text object
      const text = new Text({
        text: "Tap around and see what happens!",
        style,
      });

      // Set the text position to the center of the rectangle
      viewport.addChild(hintBox);
      viewport.addChild(text);
      // Set the anchor to the center of the text
      text.anchor.set(0.5);
      // Calculate the center of the rectangle
      text.x = hintBox.x + hintBox.width / 2;
      text.y = hintBox.y + hintBox.height / 2;

      const hintBox2 = new Graphics();
      hintBox2.beginFill(0xffffff, 0.2); // The color and opacity of the background
      hintBox2.drawRoundedRect(0, 0, 250, 55, 20); // x, y, width, height, radius
      hintBox2.endFill();
      hintBox2.x = app.screen.width / 2 - hintBox2.width / 2;
      hintBox2.y = background.y + 160;

      hintBox.filters = [
        new DropShadowFilter({
          // The color of the shadow (a hex value)
          color: 0x000000,
          // The blur amount of the shadow
          blur: 10,
          // The opacity of the shadow
          alpha: 0.5,
        }),
      ];
      const text2 = new Text({
        text: `🎪 Click here for latest Parliament news`,
        style,
      });
      // Set the text position to the center of the rectangle
      viewport.addChild(hintBox2);
      viewport.addChild(text2);
      // Set the anchor to the center of the text
      text2.anchor.set(0.5);
      // Calculate the center of the rectangle
      text2.x = hintBox2.x + hintBox2.width / 2;
      text2.y = hintBox2.y + hintBox2.height / 2;
      hintBox2.eventMode = "static";
      hintBox2.cursor = "pointer";
      text2.eventMode = "static";
      text2.cursor = "pointer";
      const openTopic = function (e) {
        e.stopPropagation();
        const path = "/topics/parliament_malaysia_2026";

        const anchor = document.getElementById(
          "open-topic-link"
        ) as HTMLAnchorElement;
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIos = /ip(ad|hone|od)/.test(userAgent);
        const isSafari = /safari/.test(userAgent);
        const isIosWebview = isIos && !isSafari;

        if (isIosWebview) {
          anchor.href = `luminews://luminews.my${path}`;
        }

        if (window.matchMedia("(min-width: 769px)").matches) {
          anchor.target = "_blank";
          anchor.href = `https://luminews.my${path}`;
        }

        anchor.click();
      };
      text2.onpointerdown = openTopic;
      hintBox2.onpointerdown = openTopic;

      const hintBox3 = new Graphics();
      hintBox3.beginFill(0xffffff, 0.2); // The color and opacity of the background
      hintBox3.drawRoundedRect(0, 0, 250, 45, 20); // x, y, width, height, radius
      hintBox3.endFill();
      hintBox3.x = app.screen.width / 2 - hintBox3.width / 2;
      hintBox3.y = background.y + 230;

      hintBox.filters = [
        new DropShadowFilter({
          // The color of the shadow (a hex value)
          color: 0x000000,
          // The blur amount of the shadow
          blur: 10,
          // The opacity of the shadow
          alpha: 0.5,
        }),
      ];
      const text3 = new Text({
        text: `▶️ Watch our anime intro`,
        style,
      });
      // Set the text position to the center of the rectangle
      viewport.addChild(hintBox3);
      viewport.addChild(text3);
      // Set the anchor to the center of the text
      text3.anchor.set(0.5);
      // Calculate the center of the rectangle
      text3.x = hintBox3.x + hintBox3.width / 2;
      text3.y = hintBox3.y + hintBox3.height / 2;
      hintBox3.eventMode = "static";
      hintBox3.cursor = "pointer";
      text3.eventMode = "static";
      text3.cursor = "pointer";
      const openAnimeVideo = function (e) {
        e.stopPropagation();
        const path = "/videos/367362";

        const anchor = document.getElementById(
          "open-anime-video-link"
        ) as HTMLAnchorElement;
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIos = /ip(ad|hone|od)/.test(userAgent);
        const isSafari = /safari/.test(userAgent);
        const isIosWebview = isIos && !isSafari;

        if (isIosWebview) {
          anchor.href = `luminews://luminews.my${path}`;
        }

        if (window.matchMedia("(min-width: 769px)").matches) {
          anchor.target = "_blank";
          anchor.href = `https://luminews.my${path}`;
        }

        anchor.click();
      };
      text3.onpointerdown = openAnimeVideo;
      hintBox3.onpointerdown = openAnimeVideo;

      const discoveredItemCountText = new Text({
        text: `0/${Object.values(CLICK_STATE).length}`,
        style: new TextStyle({
          fontFamily: "Poppins",
          fontSize: 45,
          fontWeight: "700",
          fill: "#ffffff",
          align: "center",
        }),
      });
      function updateDiscoveredItemCountText(item) {
        if (CLICK_STATE[item] != undefined) {
          CLICK_STATE[item] = true;
          const states = Object.values(CLICK_STATE);
          const count = states.filter((state) => state == true).length;

          discoveredItemCountText.text = `${count}/${states.length}`;
        }
      }

      viewport.addChild(discoveredItemCountText);
      // Set the anchor to the center of the text
      discoveredItemCountText.anchor.set(0.5);
      // Calculate the center of the rectangle
      discoveredItemCountText.x = app.screen.width / 2;
      discoveredItemCountText.y = hintBox.y - 80;

      const discoveredItemText = new Text({
        text: "items discovered",
        style: new TextStyle({
          fontFamily: "Poppins",
          fontSize: 16,
          fill: "#ffffff",
          align: "center",
        }),
      });
      viewport.addChild(discoveredItemText);
      // Set the anchor to the center of the text
      discoveredItemText.anchor.set(0.5);
      // Calculate the center of the rectangle
      discoveredItemText.x = app.screen.width / 2;
      discoveredItemText.y = discoveredItemCountText.y + 40;
    })();
  }, []);

  return (
    <div className="relative z-[60] font-poppins">
      <NextSeo
        title="Lumi News | Parliament Playground"
        description="Wake a sleepy MP, bang the hammer, and discover fun facts every day!"
        openGraph={{
          url: "https://luminews.my/lumi-parliament-playground",
          title: "Lumi News | Parliament Playground",
          description:
            "Wake a sleepy MP, bang the hammer, and discover fun facts every day!",
          images: [
            {
              url: "https://luminews.my/lumi_parliament_metaimage.webp",
              alt: "Lumi News | Parliament Playground",
            },
          ],
        }}
      />

      <div id="canvas"></div>
      <a
        className="hidden"
        id="open-anime-video-link"
        href={`https://luminews.my/videos/367362`}
      >
        Link to anime intro
      </a>
      <a
        className="hidden"
        id="open-topic-link"
        href={`https://luminews.my/topics/parliament_malaysia_2026`}
      >
        Link to topic
      </a>
      <a
        className="hidden"
        id="open-short-link"
        href={`https://luminews.my/shorts/380212?playlistId=lumi-parliament}`}
      >
        Link to shorts
      </a>
      <div
        id="coverDiv"
        className={`fixed top-0 left-0 flex h-full w-full ${
          !showFact && "translate-x-full"
        } transform justify-center  bg-white transition-transform duration-700 ease-in-out`}
      >
        <div className="absolute right-7 top-7 cursor-pointer">
          <XMarkIcon
            onClick={closeFactPage}
            className="h-8 w-8 text-primary3"
          />
        </div>
        <div className="mt-[20%] max-w-[500px] lg:mt-[15%]">
          {/* eslint-disable @next/next/no-img-element */}
          <img
            className="mx-auto w-[250px]"
            alt="nerd gumi"
            src={"/pixijs/nerd gumi.png"}
          />
          <h3 className="mx-auto mt-2 w-[80%] text-center text-2xl font-bold leading-9">
            {chosenFact && chosenFact.question}
          </h3>
          <p className="mt-4 px-8 text-center text-lg text-text-medium">
            {chosenFact && chosenFact.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

function createGlowGraphic(x, y) {
  const graphics = new Graphics();
  const color = 0xffffff;
  const radius = 35;
  // Outer glow layers (less opaque, larger)
  graphics.beginFill(color, 0.15); // Low opacity
  graphics.drawCircle(x, y, radius + 20);
  graphics.endFill();

  graphics.beginFill(color, 0.3); // Medium opacity
  graphics.drawCircle(x, y, radius + 10);
  graphics.endFill();

  // Inner glow layer (more opaque, smaller)
  graphics.beginFill(color, 0.6);
  graphics.drawCircle(x, y, radius + 5);
  graphics.endFill();

  // Core shape (solid)
  graphics.beginFill(0xffffff); // Bright white core
  graphics.drawCircle(x, y, radius);
  graphics.endFill();

  const blurFilter = new BlurFilter();
  blurFilter.blur = 15; // Adjust the blur amount
  graphics.filters = [blurFilter];

  return graphics;
}

export default Home;
